import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import {
  ScrollText, ChevronDown, ChevronUp, Search, Loader2, Save,
  ArrowUp, ArrowDown, Globe,
} from 'lucide-react';

const ACTIONABLE_LEVELS = new Set(['WARNING', 'ERROR', 'CRITICAL']);

const LOG_LEVELS = ['DEBUG', 'INFO', 'PROMPT', 'WARNING', 'ERROR', 'CRITICAL', 'OFF'];
const OVERRIDE_LEVELS = ['DEFAULT', ...LOG_LEVELS];
const FILTER_LEVELS = ['ALL', 'BLANK', 'DEBUG', 'INFO', 'PROMPT', 'WARNING', 'ERROR', 'CRITICAL'];
const LEVEL_NUMERIC: Record<string, number> = {
  'DEBUG': 10, 'INFO': 20, 'PROMPT': 25, 'WARNING': 30, 'ERROR': 40, 'CRITICAL': 50,
};
const LIMIT_OPTIONS = [50, 100, 250, 500, 1000, 2000];

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Chicago', label: 'US Central' },
  { value: 'America/Denver', label: 'US Mountain' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'America/Anchorage', label: 'US Alaska' },
  { value: 'Pacific/Honolulu', label: 'US Hawaii' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Europe/Berlin', label: 'Berlin' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Asia/Kolkata', label: 'Kolkata' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

type SortField = 'timestamp' | 'level' | 'function' | 'user_id' | 'message';
type SortDir = 'asc' | 'desc';

interface LogEntry {
  timestamp: string;
  level: string;
  function: string;
  user_id: string;
  message: string;
  extras?: Record<string, string>;
}

function levelColor(level: string): string {
  switch (level?.toUpperCase()) {
    case 'DEBUG': return 'text-gray-500';
    case 'INFO': return 'text-blue-600';
    case 'PROMPT': return 'text-purple-600';
    case 'WARNING': return 'text-amber-600';
    case 'ERROR': return 'text-red-600';
    case 'CRITICAL': return 'text-red-700 font-bold';
    default: return 'text-gray-700';
  }
}

function formatTimestamp(ts: string, timezone: string): string {
  try {
    // CloudWatch timestamps are UTC but may lack timezone indicator
    // (e.g., "2026-03-10 14:23:45.123"). Normalize to ISO 8601 UTC format.
    let normalized = ts;
    if (!/Z$/.test(normalized) && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
      normalized = normalized.replace(' ', 'T') + 'Z';
    }
    const date = new Date(normalized);
    // Format with sub-second precision for chronological ordering
    const base = date.toLocaleString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    // Append milliseconds
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${base}.${ms}`;
  } catch {
    return ts;
  }
}

function stripPrefix(name: string): string {
  return name.replace(/^skillsnap-/, '');
}

function truncateUuid(uuid: string): string {
  if (uuid && uuid.length > 12) {
    return uuid.substring(0, 8) + '...';
  }
  return uuid || '-';
}

export function LogViewer() {
  const navigate = useNavigate();

  // Accordion state
  const [configOpen, setConfigOpen] = useState(false);

  // Log config state
  const [defaultLevel, setDefaultLevel] = useState('INFO');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [functions, setFunctions] = useState<string[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Query state
  const [queryFunction, setQueryFunction] = useState('');
  const [queryMode, setQueryMode] = useState<'recent' | 'range'>('recent');
  const [recentLimit, setRecentLimit] = useState(100);
  const [startTime, setStartTime] = useState(() => {
    const d = new Date(Date.now() - 3600000);
    return d.toISOString().slice(0, 16);
  });
  const [endTime, setEndTime] = useState(() => {
    return new Date().toISOString().slice(0, 16);
  });
  const [userIdFilter, setUserIdFilter] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [levelOnly, setLevelOnly] = useState(false);

  // Timezone state
  const [timezone, setTimezone] = useState('UTC');

  // Results state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [querying, setQuerying] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await api.getLogConfig();
      setDefaultLevel(data.config?.default_level || 'INFO');
      setOverrides(data.config?.overrides || {});
      setFunctions(data.functions || []);
      setConfigLoaded(true);
    } catch (err) {
      console.error('Failed to load log config:', err);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Load timezone preference on mount
  useEffect(() => {
    api.getPreferences().then(prefs => {
      if (prefs.admin_timezone) {
        setTimezone(prefs.admin_timezone);
      }
    }).catch(err => {
      console.error('Failed to load timezone preference:', err);
    });
  }, []);

  const handleTimezoneChange = (tz: string) => {
    setTimezone(tz);
    api.updatePreferences({ admin_timezone: tz }).catch(err => {
      console.error('Failed to save timezone preference:', err);
    });
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      const filteredOverrides: Record<string, string> = {};
      for (const [fn, level] of Object.entries(overrides)) {
        if (level !== 'DEFAULT') {
          filteredOverrides[fn] = level;
        }
      }
      await api.updateLogConfig({ default_level: defaultLevel, overrides: filteredOverrides });
    } catch (err) {
      console.error('Failed to save log config:', err);
    } finally {
      setConfigSaving(false);
    }
  };

  const handleLookup = async () => {
    if (!userIdFilter.trim()) return;
    setLookingUp(true);
    try {
      const data = await api.lookupUser(userIdFilter.trim());
      if (data.userid) {
        setUserIdFilter(data.userid);
      }
    } catch (err) {
      console.error('User lookup failed:', err);
    } finally {
      setLookingUp(false);
    }
  };

  const queryLogs = async () => {
    setQuerying(true);
    setHasQueried(true);
    try {
      const params: { function_name?: string; mode: string; limit?: number; start_time?: string; end_time?: string; user_id?: string } = {
        mode: queryMode,
      };
      if (queryFunction) params.function_name = queryFunction;
      if (queryMode === 'recent') {
        params.limit = recentLimit;
      } else {
        params.start_time = new Date(startTime).toISOString();
        params.end_time = new Date(endTime).toISOString();
      }
      if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
      const data = await api.queryLogs(params);
      setLogs(data.logs || []);
      setExpandedRows(new Set());
    } catch (err) {
      console.error('Failed to query logs:', err);
      setLogs([]);
    } finally {
      setQuerying(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'timestamp' ? 'desc' : 'asc');
    }
  };

  const sortedLogs = useMemo(() => {
    // Apply level filter
    let filtered = logs;
    if (levelFilter !== 'ALL') {
      if (levelFilter === 'BLANK') {
        // Show only entries with no/empty level
        filtered = logs.filter(l => !l.level || l.level.trim() === '');
      } else {
        const threshold = LEVEL_NUMERIC[levelFilter] || 0;
        if (levelOnly) {
          // Only: show exact match
          filtered = logs.filter(l => (l.level || '').toUpperCase() === levelFilter);
        } else {
          // Show selected level and above
          filtered = logs.filter(l => {
            const n = LEVEL_NUMERIC[(l.level || '').toUpperCase()] || 0;
            return n >= threshold;
          });
        }
      }
    }

    const sorted = [...filtered].sort((a, b) => {
      let aVal: string, bVal: string;
      switch (sortField) {
        case 'timestamp': aVal = a.timestamp; bVal = b.timestamp; break;
        case 'level': aVal = a.level; bVal = b.level; break;
        case 'function': aVal = a.function; bVal = b.function; break;
        case 'user_id': aVal = a.user_id || ''; bVal = b.user_id || ''; break;
        case 'message': aVal = a.message; bVal = b.message; break;
        default: return 0;
      }
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [logs, sortField, sortDir, levelFilter, levelOnly]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline ml-1" />
      : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  const showLookupButton = userIdFilter.trim() &&
    (userIdFilter.includes('@') || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userIdFilter));

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <ScrollText className="w-8 h-8 text-violet-600" />
          <h1 className="text-2xl font-bold text-gray-900">Log Viewer</h1>
          <div className="ml-auto flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <select
              value={timezone}
              onChange={e => handleTimezoneChange(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            >
              {TIMEZONE_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label} ({tz.value})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Section A: Logging Level Controls (Accordion) */}
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => setConfigOpen(!configOpen)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <span className="text-lg font-semibold text-gray-900">Logging Level Controls</span>
            {configOpen
              ? <ChevronUp className="w-5 h-5 text-gray-500" />
              : <ChevronDown className="w-5 h-5 text-gray-500" />
            }
          </button>

          {configOpen && (
            <div className="px-6 pb-6 border-t border-gray-100">
              {configLoading && !configLoaded ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                </div>
              ) : (
                <>
                  {/* Default Level */}
                  <div className="flex items-center gap-3 mt-4 mb-6">
                    <label className="text-sm font-medium text-gray-700 w-28">Default Level</label>
                    <select
                      value={defaultLevel}
                      onChange={e => setDefaultLevel(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    >
                      {LOG_LEVELS.map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* Per-function overrides */}
                  <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium text-gray-600">Function</th>
                          <th className="text-left px-4 py-2 font-medium text-gray-600 w-40">Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {functions.map(fn => (
                          <tr key={fn} className="border-t border-gray-100">
                            <td className="px-4 py-1.5 text-gray-800 font-mono text-xs">{fn}</td>
                            <td className="px-4 py-1.5">
                              <select
                                value={overrides[fn] || 'DEFAULT'}
                                onChange={e => setOverrides(prev => ({ ...prev, [fn]: e.target.value }))}
                                className="border border-gray-300 rounded px-2 py-1 text-xs w-full focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                              >
                                {OVERRIDE_LEVELS.map(l => (
                                  <option key={l} value={l}>{l}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <button
                    onClick={saveConfig}
                    disabled={configSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors text-sm"
                  >
                    {configSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Configuration
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Section B: Retrieve Logs */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Retrieve Logs</h2>

          {/* Function selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Function</label>
            <select
              value={queryFunction}
              onChange={e => setQueryFunction(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full max-w-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            >
              <option value="">All Functions</option>
              {functions.map(fn => (
                <option key={fn} value={fn}>{fn}</option>
              ))}
            </select>
          </div>

          {/* User ID filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by User ID</label>
            <div className="flex items-center gap-2 max-w-md">
              <input
                type="text"
                value={userIdFilter}
                onChange={e => setUserIdFilter(e.target.value)}
                placeholder="All (no filter)"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm flex-1 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
              {showLookupButton && (
                <button
                  onClick={handleLookup}
                  disabled={lookingUp}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {lookingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                  Lookup
                </button>
              )}
            </div>
          </div>

          {/* Log level filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Log Level</label>
            <div className="flex items-center gap-3 max-w-md">
              <select
                value={levelFilter}
                onChange={e => setLevelFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              >
                {FILTER_LEVELS.map(l => (
                  <option key={l} value={l}>{l === 'ALL' ? 'All' : l}</option>
                ))}
              </select>
              {levelFilter !== 'ALL' && levelFilter !== 'BLANK' && (
                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={levelOnly}
                    onChange={e => setLevelOnly(e.target.checked)}
                    className="accent-violet-600"
                  />
                  Only
                </label>
              )}
              {levelFilter !== 'ALL' && (
                <span className="text-xs text-gray-500">
                  {levelFilter === 'BLANK'
                    ? 'Showing entries with no log level (unstructured)'
                    : levelOnly ? `Showing only ${levelFilter}` : `Showing ${levelFilter} and above`}
                </span>
              )}
            </div>
          </div>

          {/* Query modes */}
          <div className="space-y-3 mb-4">
            {/* Option A: Recent */}
            <div
              onClick={() => setQueryMode('recent')}
              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                queryMode === 'recent'
                  ? 'border-violet-300 bg-violet-50'
                  : 'border-gray-200 bg-gray-50 opacity-50'
              }`}
            >
              <input
                type="radio"
                checked={queryMode === 'recent'}
                onChange={() => setQueryMode('recent')}
                className="accent-violet-600"
              />
              <span className="text-sm text-gray-700">Get most recent</span>
              <select
                value={recentLimit}
                onChange={e => setRecentLimit(Number(e.target.value))}
                disabled={queryMode !== 'recent'}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
              >
                {LIMIT_OPTIONS.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-sm text-gray-700">logs</span>
            </div>

            {/* Option B: Date range */}
            <div
              onClick={() => setQueryMode('range')}
              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                queryMode === 'range'
                  ? 'border-violet-300 bg-violet-50'
                  : 'border-gray-200 bg-gray-50 opacity-50'
              }`}
            >
              <input
                type="radio"
                checked={queryMode === 'range'}
                onChange={() => setQueryMode('range')}
                className="accent-violet-600"
              />
              <span className="text-sm text-gray-700">Get all logs between</span>
              <input
                type="datetime-local"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                disabled={queryMode !== 'range'}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
              />
              <span className="text-sm text-gray-700">and</span>
              <input
                type="datetime-local"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                disabled={queryMode !== 'range'}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Refresh button */}
          <button
            onClick={queryLogs}
            disabled={querying}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors text-sm"
          >
            {querying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Refresh Logs
          </button>
        </div>

        {/* Results */}
        {querying && (
          <div className="bg-white rounded-lg shadow p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
            <span className="ml-2 text-gray-600">Loading logs...</span>
          </div>
        )}

        {!querying && hasQueried && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-3 border-b border-gray-200">
              <span className="text-sm text-gray-600">
                Showing {sortedLogs.length}{sortedLogs.length !== logs.length ? ` of ${logs.length}` : ''} logs
              </span>
            </div>

            {sortedLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No logs found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        onClick={() => toggleSort('timestamp')}
                        className="text-left px-4 py-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      >
                        Timestamp <SortIcon field="timestamp" />
                      </th>
                      <th
                        onClick={() => toggleSort('level')}
                        className="text-left px-4 py-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      >
                        Level <SortIcon field="level" />
                      </th>
                      <th
                        onClick={() => toggleSort('function')}
                        className="text-left px-4 py-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      >
                        Function <SortIcon field="function" />
                      </th>
                      <th
                        onClick={() => toggleSort('user_id')}
                        className="text-left px-4 py-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      >
                        User ID <SortIcon field="user_id" />
                      </th>
                      <th
                        onClick={() => toggleSort('message')}
                        className="text-left px-4 py-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      >
                        Message <SortIcon field="message" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLogs.map((log, i) => {
                      const hasExtras = log.extras && Object.keys(log.extras).length > 0;
                      const isExpanded = expandedRows.has(i);
                      return (
                        <React.Fragment key={i}>
                          <tr
                            className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${hasExtras ? 'cursor-pointer hover:bg-violet-50' : ''}`}
                            onClick={hasExtras ? () => setExpandedRows(prev => {
                              const next = new Set(prev);
                              if (next.has(i)) next.delete(i); else next.add(i);
                              return next;
                            }) : undefined}
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-gray-700 font-mono text-xs">
                              {formatTimestamp(log.timestamp, timezone)}
                            </td>
                            <td className={`px-4 py-2 whitespace-nowrap ${levelColor(log.level)}`}>
                              {ACTIONABLE_LEVELS.has((log.level || '').toUpperCase()) ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const fnName = stripPrefix(log.function);
                                    const area = fnName.startsWith('gen') ? 'generation'
                                      : fnName.startsWith('admin') ? 'admin'
                                      : fnName.startsWith('job') ? 'backend'
                                      : fnName.startsWith('resume') ? 'backend'
                                      : 'backend';
                                    navigate('/admin/f47ac10b58cc/work-queue', {
                                      state: {
                                        prefill: true,
                                        target_area: area,
                                        target_component: fnName,
                                        request_text: `[${log.level}] from ${log.function} at ${log.timestamp}:\n${log.message}\n\nPlease investigate, fix, test, deploy, and sync with git.`,
                                      },
                                    });
                                  }}
                                  className={`underline hover:opacity-70 cursor-pointer ${levelColor(log.level)}`}
                                  title="Create Work Queue item from this log entry"
                                >
                                  {log.level}
                                </button>
                              ) : (
                                log.level
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-700 font-mono text-xs">
                              {stripPrefix(log.function)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-600 text-xs" title={log.user_id}>
                              {truncateUuid(log.user_id)}
                            </td>
                            <td className="px-4 py-2 text-gray-800 font-mono text-xs break-words whitespace-pre-wrap max-w-xl">
                              {hasExtras && (
                                <span className="inline-block mr-1.5">
                                  {isExpanded ? <ChevronUp className="w-3 h-3 inline text-violet-500" /> : <ChevronDown className="w-3 h-3 inline text-violet-500" />}
                                </span>
                              )}
                              {log.message}
                            </td>
                          </tr>
                          {isExpanded && hasExtras && (
                            <tr className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td colSpan={5} className="px-4 pb-3">
                                <div className="ml-4 border-l-2 border-violet-300 pl-4 py-2 space-y-2">
                                  {Object.entries(log.extras!).map(([key, value]) => (
                                    <div key={key}>
                                      <span className="text-xs font-semibold text-violet-700">{key}:</span>
                                      <pre className="mt-0.5 text-xs text-gray-700 whitespace-pre-wrap break-words bg-gray-100 rounded p-2 max-h-96 overflow-y-auto">
                                        {value}
                                      </pre>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
